import { formatPercent } from '../../utils/percent';
import {
  BODY_FONT,
  canvas,
  canvasDefs,
  escapeXml,
  FOOTER_RULE_Y,
  giraffe,
  HEAD_FONT,
  headerSparkles,
  LIGHT_SKIN,
  planAndBrandFooter,
  type Skin,
  statTile,
  surface,
  trophy,
  twoToneTitle,
  wrapText,
} from './cardSkin';

interface DomainCompletionCardProps {
  domainName?: string;
  pathsCompleted?: number;
  lessonsCompleted?: number;
  pointsEarned?: number;
  averageScore?: number;
  isPremium?: boolean;
  completionDate?: string | null;
  quoteIndex?: number;
}

/**
 * DomainCompletionCard ("MODULE COMPLETE!")
 *
 * The shareable image for finishing a module, drawn as SVG so the Share
 * button can export it (CelebrationModal -> svgToBase64Png) at the same
 * 1080 x 1350 (4:5) it shows on screen.
 *
 * The course card's skin (variant A, #1078; David 2026-10-25): the warm
 * canvas, the trophy medallion and calmer sparkles, the two-tone heading, a
 * white surface holding the graduated giraffe, the module's name and three
 * stat tiles, then the Rosenberg quote and the plan badge and brand footer
 * (cardSkin.ts, shared with the course card).
 *
 * Fonts and the giraffe: Share inlines both into the exported copy, as for
 * the course card (CelebrationModal).
 */

const SURFACE = { x: 60, y: 262, width: 960, height: 572 } as const;
/** The module title wraps at this many characters, two lines at most. */
const TITLE_MAX_CHARS = 26;
const TITLE_LINE_GAP = 52;
const QUOTE_MAX_CHARS = 60;
const QUOTE_LINE_GAP = 40;
/** From the quote's last line to the attribution's baseline. */
const QUOTE_TO_AUTHOR = 70;

/**
 * The card's accessible name (b-022): its drawn text reads as fragments.
 * The same defaults as domainCardSvg, so the name says what is drawn.
 */
export function domainCardLabel({
  domainName = 'Introduction to NVC',
  pathsCompleted = 1,
  pointsEarned = 450,
  averageScore = 85,
}: DomainCompletionCardProps): string {
  return `Module complete: ${domainName}. Units completed ${pathsCompleted}, average score ${formatPercent(averageScore)}, ${pointsEarned.toLocaleString()} points earned`;
}

export function domainCardSvg(
  {
    domainName = 'Introduction to NVC',
    pathsCompleted = 1,
    lessonsCompleted: _lessonsCompleted = 12,
    pointsEarned = 450,
    averageScore = 85,
    isPremium = false, // kept for backwards compatibility, represents any paid plan
    completionDate: _completionDate = null,
    quoteIndex = 0,
  }: DomainCompletionCardProps,
  C: Skin = LIGHT_SKIN,
): string {
  // The module's name, two lines at most, centred on its slot under the giraffe.
  const titleLines = wrapText(domainName, TITLE_MAX_CHARS).slice(0, 2);
  const titleTop = SURFACE.y + (titleLines.length > 1 ? 226 : 250);

  // Marshall Rosenberg quotes
  const quotes = [
    {
      text: 'Every criticism, judgment, diagnosis, and expression of anger is the tragic expression of an unmet need.',
      author: 'Marshall Rosenberg',
    },
    {
      text: 'To practice the process of conflict resolution, we must completely abandon the goal of getting people to do what we want.',
      author: 'Marshall B. Rosenberg',
    },
    {
      text: 'Anger is a result of life-alienating thinking that is disconnected from needs. It indicates that we have moved up to our head to analyze and judge somebody rather than focus on what we are needing and not getting.',
      author: 'Marshall B. Rosenberg',
    },
    {
      text: 'What I want in my life is compassion, a flow between myself and others based on a mutual giving from the heart.',
      author: 'Marshall B. Rosenberg',
    },
    {
      text: 'It is a rare human being who can maintain focus on our needs when we are expressing them through images of their wrongness.',
      author: 'Marshall Rosenberg',
    },
    {
      text: 'Life alienating communication... traps us into a world of ideas about rightness and wrongness—a world of judgments. It is a language rich with words that classify and dichotomize people and their actions.',
      author: 'Marshall Rosenberg',
    },
    {
      text: "Let's not judge each other right and wrong. Let's communicate with one another in a way that allows us to continue to enjoy contributing to one another's well-being.",
      author: 'Marshall Rosenberg',
    },
    {
      text: "When we hear the other person's feelings and needs, we recognize our common humanity.",
      author: 'Marshall Rosenberg',
    },
  ];

  // Use the provided quote index (fixed when modal opens, won't change on re-render)
  const selectedQuote = quotes[quoteIndex % quotes.length];

  // The quote and its attribution, centred between the surface and the footer.
  const quoteLines = wrapText(selectedQuote.text, QUOTE_MAX_CHARS);
  const surfaceBottom = SURFACE.y + SURFACE.height;
  const quoteHeight = (quoteLines.length - 1) * QUOTE_LINE_GAP + QUOTE_TO_AUTHOR;
  const quoteTop = Math.round((surfaceBottom + FOOTER_RULE_Y) / 2 - quoteHeight / 2 + 10);
  const authorY = quoteTop + quoteHeight;
  const tilesY = SURFACE.y + 352;

  const svgContent = `
    <defs>${canvasDefs('eeModule', C)}
      <linearGradient id="eeModuleAccent" x1="0" x2="1" y1="0" y2="0">
        <stop offset="0%" stop-color="${C.orangeLight}"/><stop offset="100%" stop-color="${C.orange}"/>
      </linearGradient>
    </defs>

    <!-- Canvas with two soft glows -->${canvas('eeModule', C)}

    <!-- Trophy and sparkles, calmer -->
    ${headerSparkles(C)}
    ${trophy(540, 100, C)}

    ${twoToneTitle('MODULE', 'COMPLETE!', 228, C)}

    <!-- The white surface: the giraffe, the module, its figures -->${surface(SURFACE.x, SURFACE.y, SURFACE.width, SURFACE.height, 'eeModule', C)}
    ${giraffe(470, SURFACE.y + 18, 140)}

    <text data-module-title="" font-family="${HEAD_FONT}" font-weight="700" font-size="46" fill="${C.navy}" text-anchor="middle">${titleLines
      .map(
        (line, index) =>
          `<tspan x="540" y="${titleTop + index * TITLE_LINE_GAP}">${escapeXml(line)}</tspan>`,
      )
      .join('')}</text>
    <rect x="490" y="${SURFACE.y + 306}" width="100" height="8" rx="4" fill="url(#eeModuleAccent)"/>

    ${statTile(110, tilesY, 'route', String(pathsCompleted), 'Units Completed', 'units', C)}
    ${statTile(405, tilesY, 'chart', formatPercent(averageScore), 'Average Score', 'average', C)}
    ${statTile(700, tilesY, 'star', pointsEarned.toLocaleString(), 'Points Earned', 'points', C)}

    <!-- Marshall Rosenberg quote: opened on the first line, closed on the last -->
    <text data-quote="" font-family="${BODY_FONT}" font-weight="400" font-style="italic" font-size="27" fill="${C.navy}" text-anchor="middle">${quoteLines
      .map(
        (line, index) =>
          `<tspan x="540" y="${quoteTop + index * QUOTE_LINE_GAP}">${index === 0 ? '"' : ''}${escapeXml(line)}${index === quoteLines.length - 1 ? '"' : ''}</tspan>`,
      )
      .join('')}</text>
    <rect x="516" y="${authorY - 40}" width="48" height="4" rx="2" fill="${C.orange}"/>
    <text data-quote-author="" x="540" y="${authorY}" font-family="${BODY_FONT}" font-weight="500" font-size="23" fill="${C.secondary}" text-anchor="middle">${escapeXml(selectedQuote.author)}</text>

    <!-- Plan badge and brand footer -->${planAndBrandFooter(isPremium, C)}
  `;

  return svgContent;
}

/**
 * The card on screen: the light skin (the share image's) unless the modal
 * hands it the live theme's (dark mode). Share exports domainCardSvg(props,
 * LIGHT_SKIN) whatever the screen shows.
 */
function DomainCompletionCard({ skin, ...props }: DomainCompletionCardProps & { skin?: Skin }) {
  return (
    <svg
      id="shareable-domain-completion-card"
      role="img"
      aria-label={domainCardLabel(props)}
      data-skin={skin && skin !== LIGHT_SKIN ? 'theme' : 'light'}
      width="100%"
      height="100%"
      viewBox="0 0 1080 1350"
      preserveAspectRatio="xMidYMid meet"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      style={{ display: 'block' }}
      dangerouslySetInnerHTML={{ __html: domainCardSvg(props, skin) }}
    />
  );
}

export default DomainCompletionCard;
